// Chat storage API for persisting chats to backend

import { resolveTenantId } from '../config/tenants';
import type { UserLocation } from './ipLocationMapper';

// Message metadata is stored as sent to /api/chats/save and returned unchanged.
// The frontend reads study_refs back, the note from the backend's study_refs event.
export interface MessageMetadata {
  study_refs?: string;
  [key: string]: unknown;
}

interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  attachments?: Array<{
    type: string;
    mime_type: string;
    data: string;
    filename: string;
    size: number;
  }>;
  metadata?: MessageMetadata;  // Support metadata (e.g., is_from_stt, tool_calls)
}

interface SaveChatRequest {
  messages: ChatMessage[];
  session_id?: string;
  chat_id?: string;
  title?: string;
  source?: string;
  tenant?: string;
  practice_slug?: string;
  request_id?: string;
  ip_location_city?: string;
  ip_location_region?: string;
  ip_location_zip?: string;
  ip_location_lat?: number;
  ip_location_lon?: number;
}

interface SaveChatResponse {
  chat_id: string;
  user_id: string;
  messages_saved: number;
  is_new_chat: boolean;
}

interface ChatData {
  id: string;
  user_id: string;
  title?: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  chat_number: number;
  message_count?: number;
  last_message_at?: string;
  metadata?: Record<string, unknown>;
  source?: string;
  tenant?: string;
}

interface ChatWithMessages extends ChatData {
  messages: Array<{
    id: string;
    chat_id: string;
    role: string;
    content: string;
    created_at: string;
    message_order: number;
    metadata?: MessageMetadata;
  }>;
}

interface ChatListResponse {
  chats: ChatData[];
  total: number;
}

interface SessionResponse {
  user_id: string;
  session_id: string;
  is_new: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export class ChatStorageAPI {
  private baseUrl: string;
  private sessionId: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    // Try to restore session from localStorage
    if (typeof window !== 'undefined') {
      this.sessionId = localStorage.getItem('chat_session_id');
      if (!this.sessionId) {
        // Generate new session ID
        this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('chat_session_id', this.sessionId);
      }
    }
  }

  getSessionId(): string | null {
    return this.sessionId;
  }

  resetSession(): void {
    // Clear session and generate new one
    this.sessionId = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('chat_session_id');
      // Generate new session ID for anonymous usage
      this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chat_session_id', this.sessionId);
    }
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.sessionId) {
      headers['Session-Id'] = this.sessionId;
    }

    return headers;
  }

  /**
   * Create or get a session for anonymous users
   */
  async initSession(): Promise<SessionResponse> {
    if (!this.sessionId) {
      throw new Error('No session ID available');
    }

    const response = await fetch(`${this.baseUrl}/api/chats/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ session_id: this.sessionId }),
    });

    if (!response.ok) {
      throw new Error(`Failed to init session: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Save chat messages to the backend
   */
  async saveChat(
    messages: ChatMessage[],
    chatId?: string,
    title?: string,
    source?: string,
    location?: UserLocation | null,
    requestId: string = crypto.randomUUID(),
  ): Promise<SaveChatResponse> {
    const request: SaveChatRequest = {
      messages,
      session_id: this.sessionId || undefined,
      chat_id: chatId,
      title,
      source,
      tenant: resolveTenantId(),
      practice_slug: chatId ? undefined : new URLSearchParams(window.location.search).get('practice') || undefined,
      request_id: requestId,
      ip_location_city: location?.city,
      ip_location_region: location?.region,
      ip_location_zip: location?.zip,
      ip_location_lat: location?.lat,
      ip_location_lon: location?.lon,
    };

    const response = await fetch(`${this.baseUrl}/api/chats/save`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to save chat: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get all chats for the current user/session
   */
  async getChats(limit: number = 50, offset: number = 0): Promise<ChatListResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      tenant: resolveTenantId(),
    });

    const response = await fetch(`${this.baseUrl}/api/chats?${params}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get chats: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get a specific chat with all its messages
   */
  async getChatWithMessages(chatId: string): Promise<ChatWithMessages> {
    const response = await fetch(`${this.baseUrl}/api/chats/${chatId}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get chat: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Update a chat (e.g., title)
   */
  async updateChat(chatId: string, updates: { title?: string; is_active?: boolean }): Promise<ChatData> {
    const response = await fetch(`${this.baseUrl}/api/chats/${chatId}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update chat: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Generate an intelligent title for a chat based on its messages
   */
  async generateChatTitle(chatId: string): Promise<ChatData> {
    const response = await fetch(`${this.baseUrl}/api/chats/${chatId}/generate-title`, {
      method: 'POST',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to generate title: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Delete a chat
   */
  async deleteChat(chatId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/chats/${chatId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete chat: ${response.status}`);
    }
  }
}

// Export a default instance
export const chatStorageAPI = new ChatStorageAPI();
